// @flow
import {
  ADD_ORDER_ITEM,
  CLEAR_ITEMS,
  REMOVE_ITEM_FROM_ORDER_BY_ID,
  SET_DISCOUNT_CODE,
  SET_PAY_IN_TERMS,
  SET_PRICE_SPECIFICATION,
  SET_PAYMENT_SPECIFICATION,
  SET_TERMS_FOR_CART_ITEM,
  SET_TERM_AGREEMENT,
  SET_CUSTOMER_DETAILS_FIELD,
  SET_ORDER_VALIDATION_ERRORS,
  SET_INVOICE_ADDRESS_FIELD, SET_CUSTOMER_DETAILS, SET_INVOICE_ADDRESS,
  SET_ORDER_CREATED,
  SET_ORDER_STATUS,
  SET_PAYMENT_METHOD_NAME,
  SET_PAYMENT_ISSUER_NAME,
  RESET,
  CLEAR_ORDER_VALIDATION_ERRORS,
  CLEAR_ORDER_CREATED,
  SET_CUSTOMER_NUMBER,
  RESET_ORDER_STATUS,
  CLEAR_PAY_IN_TERMS,
  SET_COMBI_DEAL_DISCOUNT_SUGGESTIONS,
  CLEAR_TERMS_FOR_ALL_CART_ITEMS,
  CUSTOMER_DETAILS_CHANGED, CLEAR_SHOP, SET_SHOP, CLEAR_ORDER_VALIDATION_ERROR_BY_PROPERTY_PATH
} from './mutation-types'

import type { PropertyError } from '../../../types/validation-result'
import type { OrderCreated } from '../../../types/order-created'
import type { OrderStatus } from '../../../types/order-status'
import type { PriceSpecification } from '../../../types/price-specification'
import type { CartState } from './index'
import type { PaymentSpecification } from '../../../types/payment-specification'
import { buildOrder } from '../../../types/order'
import Vue from 'vue'
import type { CustomerDetails } from '../../../types/customer-details'
import type { Address } from '../../../types/address'
import type { CustomerNumber } from '../../../types/customer-number'
import { ORDER_STATUS_ACCEPTED } from '../../../order-api/order-api-client-constants'
import type { CombiDealDiscountSuggestions } from '../../../types/combi-deal-discount-suggestions'
import type {Shop} from '../shop/types'

const isCustomerDetailsExisting = (state: CartState) => state.order && state.order.customerDetails

export default {
  [ADD_ORDER_ITEM]: (state: CartState, indexedOrderItemObj) => {
    state.order.items = { ...state.order.items, [Object.keys(indexedOrderItemObj)[0]]: Object.values(indexedOrderItemObj)[0] }
  },
  [SET_ORDER_VALIDATION_ERRORS]: (state: CartState, errors: PropertyError[]) => {
    state.orderValidationErrors = errors
  },
  [CLEAR_ORDER_VALIDATION_ERRORS]: (state: CartState) => {
    state.orderValidationErrors = []
  },
  [CLEAR_ORDER_VALIDATION_ERROR_BY_PROPERTY_PATH]: (state: CartState, propertyPath: string) => {
    state.orderValidationErrors = state.orderValidationErrors.filter(
      validationError => validationError.propertyPath !== propertyPath
    )
  },
  [REMOVE_ITEM_FROM_ORDER_BY_ID]: (state: CartState, cartItemId: string) => {
    delete state.order.items[cartItemId]
  },
  [CLEAR_ITEMS]: (state: CartState) => {
    state.order.items = {}
  },
  [SET_PRICE_SPECIFICATION]: (state, priceSpecification: PriceSpecification) => {
    state.priceSpecification = priceSpecification
  },
  [SET_COMBI_DEAL_DISCOUNT_SUGGESTIONS]: (state, combiDealDiscountSuggestions: CombiDealDiscountSuggestions) => {
    state.combiDealDiscountSuggestions = combiDealDiscountSuggestions
  },
  [SET_PAY_IN_TERMS]: (state: CartState, newValue: boolean) => {
    state.paymentTypeSelected = true
    state.payInTerms = newValue
  },
  [CLEAR_PAY_IN_TERMS]: (state: CartState) => {
    state.paymentTypeSelected = false
    state.payInTerms = false
  },
  [SET_DISCOUNT_CODE]: (state: CartState, discountCode: string) => {
    state.order.discountCode = discountCode
  },
  [SET_PAYMENT_SPECIFICATION]: (state, paymentSpecification: PaymentSpecification) => {
    state.paymentSpecification = paymentSpecification
  },
  [SET_TERMS_FOR_CART_ITEM]: (state: CartState, data: { terms: number, cartProductItem: {orderItemId: string}}) => {
    const { terms, cartProductItem } = data
    if (cartProductItem.orderItemId in state.order.items) {
      state.order.items[cartProductItem.orderItemId].nrOfTerms = terms
      state.order.items[cartProductItem.orderItemId].payInTerms = terms > 1
    }
  },
  [CLEAR_TERMS_FOR_ALL_CART_ITEMS]: (state: CartState) => {
    Object.keys(state.order.items).forEach(orderItemId => {
      state.order.items[orderItemId].nrOfTerms = 1
      state.order.items[orderItemId].payInTerms = false
    })
  },
  [SET_ORDER_CREATED]: (state: CartState, orderCreated: OrderCreated) => {
    state.orderCreated = orderCreated
    state.order.id = orderCreated.orderId
    state.order.token = orderCreated.orderToken

    state.order.items = Object.entries(state.order.items).reduce(
      (newItems, [oldId, oldItem]) => {
        const newId = orderCreated.items[oldId].id
        newItems[newId] = {...oldItem}
        newItems[newId].id = newId

        return newItems
      },
      {}
    )
  },
  [CLEAR_ORDER_CREATED]: (state: CartState) => {
    state.order.id = null
    state.order.token = null
    Object.keys(state.order.items).forEach(key => {
      state.order.items[key].id = null
    })
    state.orderCreated = null
  },
  [SET_ORDER_STATUS]: (state: CartState, orderStatus: OrderStatus) => {
    state.orderStatus = orderStatus
  },
  [RESET]: (state: CartState) => {
    Object.assign(state, {
      paymentTypeSelected: false,
      order: buildOrder(),
      orderValidationErrors: [],
      orderCreated: null,
      orderStatus: null,
      priceSpecification: null,
      paymentSpecification: null,
      payInTerms: false
    })
  },
  [SET_TERM_AGREEMENT]: (state: CartState, newValue: boolean) => {
    state.order.approvePaymentInTerms = newValue
  },
  [SET_CUSTOMER_DETAILS]: (state: CartState, customerDetails: CustomerDetails) => {
    state.order.customerDetails = customerDetails
  },
  [SET_CUSTOMER_DETAILS_FIELD]: (state: CartState, { name, value }: { name: string, value: any }) => {
    if (!isCustomerDetailsExisting(state)) {
      Vue.$log.debug('Can not set customer details field if there is no customer details object')
      return
    }
    state.order.customerDetails[name] = value
  },
  [SET_INVOICE_ADDRESS]: (state: CartState, invoiceAddress: Address) => {
    state.order.invoiceAddress = invoiceAddress
  },
  [SET_INVOICE_ADDRESS_FIELD]: (state: CartState, { name, value }: { name: string, value: any }) => {
    state.order.invoiceAddress[name] = value
  },
  [SET_PAYMENT_METHOD_NAME]: (state: CartState, paymentMethodName: ?string) => {
    state.order.paymentMethodName = paymentMethodName
  },
  [SET_PAYMENT_ISSUER_NAME]: (state: CartState, paymentIssuerName: ?string) => {
    state.order.paymentIssuerName = paymentIssuerName
  },
  [SET_CUSTOMER_NUMBER]: (state: CartState, customerNumber: ?CustomerNumber) => {
    if (state.order && state.order.customerDetails) {
      state.order.customerDetails.customerNumber = customerNumber
    }
  },
  [RESET_ORDER_STATUS]: (state: CartState) => {
    if (state.orderStatus) {
      state.orderStatus.status = ORDER_STATUS_ACCEPTED
    }
  },
  [CUSTOMER_DETAILS_CHANGED]: (state: CartState, changed: boolean) => {
    state.customerDetailsChanged = changed
  },
  [SET_SHOP]: (state: CartState, shop: Shop) => {
    state.shop = shop
  },
  [CLEAR_SHOP]: (state: CartState) => {
    state.shop = null
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/cart/mutations.js